import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Car, Fuel, Wrench, Shield, Edit2, Trash2 } from 'lucide-react';

const TYPE_LABEL = { fuel: 'Fuel', maintenance: 'Maintenance', insurance: 'Insurance', accident: 'Accident', other: 'Other' };
const TYPE_ICON = { fuel: Fuel, maintenance: Wrench, insurance: Shield, accident: Car, other: Car };

const Fleet = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showV, setShowV] = useState(false);
  const [showL, setShowL] = useState(false);
  const [editV, setEditV] = useState(null);
  const [vf, setVf] = useState({ plate_number: '', name: '', vehicle_type: 'car', make: '', model: '', year: 2024, purchase_value: 0, default_driver_id: '', insurance_expiry: '', registration_expiry: '', odometer_km: 0 });
  const [lf, setLf] = useState({ vehicle_id: '', log_type: 'fuel', log_date: '', amount: 0, project_id: '', driver_id: '', description: '', fuel_liters: 0, odometer_km: 0 });

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        axiosInstance.get('/vehicles'),
        axiosInstance.get('/fleet-logs'),
        axiosInstance.get('/employees'),
        axiosInstance.get('/projects'),
      ]);
      setVehicles(a.data || []); setLogs(b.data || []); setEmployees(c.data || []); setProjects(d.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const saveVehicle = async (e) => {
    e.preventDefault();
    try {
      if (editV) {
        await axiosInstance.patch(`/vehicles/${editV.id}`, vf);
        toast.success('Updated');
      } else {
        await axiosInstance.post('/vehicles', vf);
        toast.success('Created');
      }
      setShowV(false); setEditV(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const delV = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try { await axiosInstance.delete(`/vehicles/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const saveLog = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/fleet-logs', { ...lf, amount: Number(lf.amount), fuel_liters: Number(lf.fuel_liters || 0), odometer_km: Number(lf.odometer_km || 0), project_id: lf.project_id || null, driver_id: lf.driver_id || null });
      toast.success('Logged'); setShowL(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Car size={24} /> {t('fleet.title')}</h1>

      <div className="bg-white rounded-xl shadow">
        <div className="border-b flex">
          <button onClick={() => setTab('vehicles')} className={`px-6 py-3 font-semibold text-sm ${tab === 'vehicles' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}>
            {t('fleet.tabs.vehicles')} ({vehicles.length})
          </button>
          <button onClick={() => setTab('logs')} className={`px-6 py-3 font-semibold text-sm ${tab === 'logs' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}>
            {t('fleet.tabs.logs')} ({logs.length})
          </button>
        </div>

        {tab === 'vehicles' ? (
          <div>
            <div className="p-4 border-b flex justify-end">
              <button data-testid="add-vehicle-btn" onClick={() => { setEditV(null); setVf({ plate_number: '', name: '', vehicle_type: 'car', make: '', model: '', year: 2024, purchase_value: 0, default_driver_id: '', insurance_expiry: '', registration_expiry: '', odometer_km: 0 }); setShowV(true); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm">
                <Plus size={16} /> {t('fleet.add_vehicle')}
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.code')}</th>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.plate')}</th>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.vehicle')}</th>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.driver')}</th>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.odometer')}</th>
                  <th className="px-4 py-3 text-left">{t('fleet.fields.insurance_expiry')}</th>
                  <th className="px-4 py-3 text-left">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{v.code}</td>
                    <td className="px-4 py-3 font-mono font-bold">{v.plate_number}</td>
                    <td className="px-4 py-3">{v.name || `${v.make} ${v.model}`} ({v.year})</td>
                    <td className="px-4 py-3 text-xs">{v.default_driver_name || '—'}</td>
                    <td className="px-4 py-3 font-mono">{(v.odometer_km || 0).toLocaleString()} km</td>
                    <td className="px-4 py-3 text-xs">{v.insurance_expiry || '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setEditV(v); setVf({ ...v }); setShowV(true); }} className="p-1 text-blue-600"><Edit2 size={14} /></button>
                      <button onClick={() => delV(v.id)} className="p-1 text-red-600 ml-1"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <div className="p-4 border-b flex justify-end">
              <button data-testid="add-fleet-log-btn" onClick={() => { setLf({ vehicle_id: '', log_type: 'fuel', log_date: new Date().toISOString().slice(0, 10), amount: 0, project_id: '', driver_id: '', description: '', fuel_liters: 0, odometer_km: 0 }); setShowL(true); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm">
                <Plus size={16} /> Log Entry
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">No</th>
                  <th className="px-4 py-3 text-left">Vehicle</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Project</th>
                  <th className="px-4 py-3 text-left">Journal</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const Icon = TYPE_ICON[l.log_type] || Car;
                  return (
                    <tr key={l.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{l.log_no}</td>
                      <td className="px-4 py-3 font-mono">{l.plate_number}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded"><Icon size={12} /> {TYPE_LABEL[l.log_type]}</span></td>
                      <td className="px-4 py-3 text-xs">{l.log_date}</td>
                      <td className="px-4 py-3 font-mono">{l.amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs">{l.project_name || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-purple-600">{l.journal_no || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showV && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveVehicle} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b"><h2 className="text-xl font-bold">{editV ? 'Edit Vehicle' : 'Add Vehicle'}</h2></div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div><label className="text-xs text-gray-600 mb-1 block">Plate Number *</label><input required value={vf.plate_number} onChange={(e) => setVf({ ...vf, plate_number: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="veh-plate" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Name / Description</label><input value={vf.name} onChange={(e) => setVf({ ...vf, name: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" placeholder="Toyota Hilux 2024" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Type</label>
                <select value={vf.vehicle_type} onChange={(e) => setVf({ ...vf, vehicle_type: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="car">Car</option><option value="pickup">Pickup</option><option value="truck">Truck</option><option value="bus">Bus</option><option value="trailer">Trailer</option><option value="other">Other</option>
                </select>
              </div>
              <div><label className="text-xs text-gray-600 mb-1 block">Make</label><input value={vf.make} onChange={(e) => setVf({ ...vf, make: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Model</label><input value={vf.model} onChange={(e) => setVf({ ...vf, model: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Year</label><input type="number" value={vf.year} onChange={(e) => setVf({ ...vf, year: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Purchase Value</label><input type="number" value={vf.purchase_value} onChange={(e) => setVf({ ...vf, purchase_value: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Default Driver</label>
                <select value={vf.default_driver_id} onChange={(e) => setVf({ ...vf, default_driver_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">— None —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-600 mb-1 block">Insurance Expiry</label><input type="date" value={vf.insurance_expiry} onChange={(e) => setVf({ ...vf, insurance_expiry: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Registration Expiry</label><input type="date" value={vf.registration_expiry} onChange={(e) => setVf({ ...vf, registration_expiry: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Odometer (km)</label><input type="number" value={vf.odometer_km} onChange={(e) => setVf({ ...vf, odometer_km: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
            </div>
            <div className="p-6 border-t flex gap-2 justify-end">
              <button type="button" onClick={() => setShowV(false)} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
              <button type="submit" data-testid="save-vehicle-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm">Save</button>
            </div>
          </form>
        </div>
      )}

      {showL && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveLog} className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
            <div className="p-6 border-b"><h2 className="text-xl font-bold">Log (Fuel / Maintenance / Insurance)</h2></div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs text-gray-600 mb-1 block">Vehicle *</label>
                <select required value={lf.vehicle_id} onChange={(e) => setLf({ ...lf, vehicle_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="log-vehicle">
                  <option value="">— Select —</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number} — {v.name || v.make}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-600 mb-1 block">Type *</label>
                <select required value={lf.log_type} onChange={(e) => setLf({ ...lf, log_type: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-600 mb-1 block">Date *</label><input type="date" required value={lf.log_date} onChange={(e) => setLf({ ...lf, log_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Amount (SAR)</label><input type="number" step="0.01" value={lf.amount} onChange={(e) => setLf({ ...lf, amount: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="log-amount" /></div>
              <div><label className="text-xs text-gray-600 mb-1 block">Odometer (km)</label><input type="number" value={lf.odometer_km} onChange={(e) => setLf({ ...lf, odometer_km: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
              {lf.log_type === 'fuel' && <div><label className="text-xs text-gray-600 mb-1 block">Fuel Liters</label><input type="number" step="0.01" value={lf.fuel_liters} onChange={(e) => setLf({ ...lf, fuel_liters: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>}
              <div><label className="text-xs text-gray-600 mb-1 block">Project (optional)</label>
                <select value={lf.project_id} onChange={(e) => setLf({ ...lf, project_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">— Auto-allocate —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-600 mb-1 block">Driver</label>
                <select value={lf.driver_id} onChange={(e) => setLf({ ...lf, driver_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">— None —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                </select>
              </div>
              <div className="col-span-2"><label className="text-xs text-gray-600 mb-1 block">Description</label><input value={lf.description} onChange={(e) => setLf({ ...lf, description: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" /></div>
            </div>
            <div className="p-6 border-t flex gap-2 justify-end">
              <button type="button" onClick={() => setShowL(false)} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
              <button type="submit" data-testid="save-fleet-log-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Fleet;
